import {
  CSV_DELIMITER,
  UTF8_BOM,
  csvAmount,
  csvField,
  formatCents,
  formatCount,
  parseMoneyToCents,
  tt,
  type MoneyInput,
} from '@agos/shared'

/**
 * The shape of trade over a window, and three figures that do not say what they mean.
 *
 * **FB-104.** The page shows two revenue totals and they differ. `/statistics/sales` answers
 * **44 807,65 €** for July; the product table under it sums to **44 736,25 €**. The gap is 71,40 €
 * and nothing on the screen accounts for it: `orders.total` is `subtotal` plus what no product line
 * carries, and `/statistics/products` reports the line revenue —
 * `sum(order_items.total_price) = sum(orders.subtotal) = 44 736,25`, checked against the database.
 * Two totals a few tens of euros apart, side by side, with no note, is how somebody spends an
 * afternoon reconciling a report that was never wrong.
 *
 * **FB-105.** The CSV export cannot be summed in the spreadsheet it is made for. It is
 * comma-delimited and writes `revenue.toFixed(2)` — a **dot** decimal separator — so a Slovak-locale
 * Excel reads every amount as text. And 124 of the 1 113 product names contain a comma themselves
 * (`Kofola čapovaná 0,3l`, `Grissini s mozzarelou, slaninou a ementálom`); legacy wraps fields in
 * quotes, which handles those, but nothing escapes an embedded quote, so the first product named
 * `Pizza "Špeciál"` shifts every column after it on that row.
 *
 * **FB-106.** `ordersPerHour` is rendered straight, and **seven of the thirteen employees have no
 * hours recorded** — they carried **906 of the month's 2 259 orders** between them, including the
 * busiest driver at 328. All seven read *0,00 orders/hour*, which sorts them last and says they did
 * nothing per hour when it means nobody clocked them in. The same missing attendance FB-101 and
 * FB-102 found on the payroll, surfacing here as a performance figure.
 */

// ─── the window ──────────────────────────────────────────────────────────────

export type PeriodKey =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'last_week'
  | 'month'
  | 'last_month'
  | 'custom'

export interface DateRange {
  from: string
  to: string
}

/** A local calendar date. `toISOString()` would render a Bratislava midnight as the day before. */
function localDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function mondayOf(date: Date): Date {
  const result = new Date(date)
  const day = result.getDay()
  result.setDate(result.getDate() - day + (day === 0 ? -6 : 1))
  return result
}

/**
 * The dates a period covers, in the restaurant's own calendar.
 *
 * Ported from legacy's `getDateRange`, which is correct — and the parameter names it is fed into are
 * where the trap is: `/statistics/*` take **`date_from` / `date_to`** while `/orders/heatmap` and
 * `/employees/performance` take **`from` / `to`**. Sending the wrong pair does not fail; the server
 * substitutes its own default window and answers with a plausible number. Asking for July with
 * `from`/`to` returns 47 404,50 € over 29 days beginning 30 June instead of 44 807,65 € over 1–28
 * July. Both shapes are built here so a caller cannot mix them up.
 */
export function rangeFor(period: PeriodKey, today: Date, custom?: DateRange): DateRange {
  if (period === 'custom' && custom !== undefined) return custom

  if (period === 'yesterday') {
    const day = new Date(today)
    day.setDate(day.getDate() - 1)
    return { from: localDate(day), to: localDate(day) }
  }
  if (period === 'week') {
    return { from: localDate(mondayOf(today)), to: localDate(today) }
  }
  if (period === 'last_week') {
    const start = mondayOf(today)
    start.setDate(start.getDate() - 7)
    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    return { from: localDate(start), to: localDate(end) }
  }
  if (period === 'month') {
    return {
      from: localDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: localDate(today),
    }
  }
  if (period === 'last_month') {
    return {
      from: localDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: localDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    }
  }
  return { from: localDate(today), to: localDate(today) }
}

/** What `/statistics/*` wants. */
export function statisticsParams(range: DateRange): { date_from: string; date_to: string } {
  return { date_from: range.from, date_to: range.to }
}

/** What `/orders/heatmap` and `/employees/performance` want. */
export function rangeParams(range: DateRange): { from: string; to: string } {
  return { from: range.from, to: range.to }
}

export function periodLabel(period: PeriodKey): string {
  if (period === 'today') return tt('Dnes')
  if (period === 'yesterday') return tt('Včera')
  if (period === 'week') return tt('Tento týždeň')
  if (period === 'last_week') return tt('Minulý týždeň')
  if (period === 'month') return tt('Tento mesiac')
  if (period === 'last_month') return tt('Minulý mesiac')
  return tt('Vlastný rozsah')
}

export const PERIODS: readonly PeriodKey[] = [
  'today',
  'yesterday',
  'week',
  'last_week',
  'month',
  'last_month',
]

// ─── what the sales endpoint answers ─────────────────────────────────────────

export interface SalesStatistics {
  totalRevenue?: MoneyInput
  totalOrders?: number
  averageOrderValue?: MoneyInput
  revenueByDay?: { date?: string; revenue?: MoneyInput; orders?: number }[]
  revenueByPaymentMethod?: { method?: string; amount?: MoneyInput }[]
}

export interface ProductStat {
  productId?: number
  productName?: string
  categoryId?: number
  categoryName?: string
  quantitySold?: number
  revenue?: MoneyInput
}

export interface CategoryStat {
  categoryId?: number
  categoryName?: string
  itemCount?: number
  orderCount?: number
  revenue?: MoneyInput
}

export interface SalesTotals {
  revenueCents: number
  orders: number
  averageCents: number
  days: number
}

/**
 * The window's totals, in cents.
 *
 * The average is recomputed from the two figures rather than taken from `averageOrderValue`, because a
 * total and a count that do not divide into the stated average is a discrepancy worth seeing —
 * `totalsAgree` reports it. On this data they agree: 44 807,65 / 2 261 = 19,8177…, reported as 19,82.
 */
export function salesTotals(sales: SalesStatistics): SalesTotals {
  const revenueCents = parseMoneyToCents(sales.totalRevenue)
  const orders = sales.totalOrders ?? 0
  return {
    revenueCents,
    orders,
    averageCents: orders > 0 ? Math.round(revenueCents / orders) : 0,
    days: (sales.revenueByDay ?? []).length,
  }
}

/** Whether the daily rows add up to the stated total. */
export function dailyRowsAgree(sales: SalesStatistics): boolean {
  const days = sales.revenueByDay ?? []
  if (days.length === 0) return true
  const summed = days.reduce((total, day) => total + parseMoneyToCents(day.revenue), 0)
  const orders = days.reduce((total, day) => total + (day.orders ?? 0), 0)
  return summed === parseMoneyToCents(sales.totalRevenue) && orders === (sales.totalOrders ?? 0)
}

/** Whether the payment split adds up to the stated total. */
export function paymentsAgree(sales: SalesStatistics): boolean {
  const methods = sales.revenueByPaymentMethod ?? []
  if (methods.length === 0) return true
  const summed = methods.reduce((total, method) => total + parseMoneyToCents(method.amount), 0)
  return summed === parseMoneyToCents(sales.totalRevenue)
}

// ─── FB-104: the two totals ──────────────────────────────────────────────────

export interface RevenueReconciliation {
  orderRevenueCents: number
  lineRevenueCents: number
  /** What the orders carry and no product line does — delivery and the like. */
  differenceCents: number
  agrees: boolean
}

/**
 * The sales total against the product total, with the gap named.
 *
 * `/statistics/sales` reports `orders.total`; `/statistics/products` reports the line revenue, which
 * equals `orders.subtotal`. The difference is real and is not an error — it is what an order carries
 * beyond its lines. Saying so is the whole point: legacy prints both figures a few centimetres apart
 * and explains neither.
 */
export function reconcileRevenue(
  sales: SalesStatistics,
  products: ProductStat[],
): RevenueReconciliation {
  const orderRevenueCents = parseMoneyToCents(sales.totalRevenue)
  const lineRevenueCents = products.reduce(
    (total, product) => total + parseMoneyToCents(product.revenue),
    0,
  )
  const differenceCents = orderRevenueCents - lineRevenueCents
  return {
    orderRevenueCents,
    lineRevenueCents,
    differenceCents,
    agrees: differenceCents === 0,
  }
}

/** Whether the category totals and the product totals tell the same story. */
export function categoriesMatchProducts(
  categories: CategoryStat[],
  products: ProductStat[],
): boolean {
  const byCategory = categories.reduce(
    (total, category) => total + parseMoneyToCents(category.revenue),
    0,
  )
  const byProduct = products.reduce(
    (total, product) => total + parseMoneyToCents(product.revenue),
    0,
  )
  return byCategory === byProduct
}

// ─── products ────────────────────────────────────────────────────────────────

export type SortKey = 'revenue' | 'quantity'

export function sortProducts(products: ProductStat[], key: SortKey): ProductStat[] {
  return [...products].sort((left, right) => {
    if (key === 'revenue') {
      return parseMoneyToCents(right.revenue) - parseMoneyToCents(left.revenue)
    }
    return (right.quantitySold ?? 0) - (left.quantitySold ?? 0)
  })
}

export interface CategoryGroup {
  categoryName: string
  revenueCents: number
  quantity: number
  products: ProductStat[]
}

/** Products under their category, the biggest category first. */
export function groupByCategory(products: ProductStat[]): CategoryGroup[] {
  const groups = new Map<string, CategoryGroup>()
  for (const product of products) {
    const categoryName = product.categoryName ?? tt('Bez kategórie')
    const group =
      groups.get(categoryName) ?? { categoryName, revenueCents: 0, quantity: 0, products: [] }
    group.revenueCents += parseMoneyToCents(product.revenue)
    group.quantity += product.quantitySold ?? 0
    group.products.push(product)
    groups.set(categoryName, group)
  }
  for (const group of groups.values()) {
    group.products = sortProducts(group.products, 'revenue')
  }
  return [...groups.values()].sort((left, right) => right.revenueCents - left.revenueCents)
}

export function categoryNames(products: ProductStat[]): string[] {
  return [...new Set(products.map((product) => product.categoryName ?? ''))]
    .filter((name) => name !== '')
    .sort((left, right) => left.localeCompare(right))
}

export function filterByCategory(products: ProductStat[], category: string | null): ProductStat[] {
  if (category === null) return products
  return products.filter((product) => product.categoryName === category)
}

/** A product's share of the revenue shown, in basis points. */
export function shareBasisPoints(product: ProductStat, totalCents: number): number {
  if (totalCents <= 0) return 0
  return Math.round((parseMoneyToCents(product.revenue) / totalCents) * 10_000)
}

// ─── FB-105: the export ──────────────────────────────────────────────────────

/** A Slovak spreadsheet expects a semicolon when the decimal separator is a comma. */
// The CSV rules live in `@agos/shared` — `/reports` has six more export buttons and had its own
// hand-rolled writer with the same defect. Re-exported here so the page imports and the tests that
// pin FB-105's behaviour keep working from one place.
export { CSV_DELIMITER, UTF8_BOM, csvAmount, csvField }

/**
 * The product export, in a shape a Slovak-locale spreadsheet can add up.
 *
 * Semicolon-delimited with comma decimals, and a UTF-8 byte-order mark so Excel reads the diacritics.
 * Legacy uses commas as the delimiter *and* dots as the decimal separator, which makes every amount
 * text — the one thing an export of amounts must not be.
 */
export function productCsv(products: ProductStat[], sortKey: SortKey): string {
  const header = [tt('Kategória'), tt('Produkt'), tt('Predaných ks'), tt('Tržba (€)')]
    .map(csvField)
    .join(CSV_DELIMITER)
  const rows = sortProducts(products, sortKey).map((product) =>
    [
      csvField(product.categoryName ?? ''),
      csvField(product.productName ?? ''),
      String(product.quantitySold ?? 0),
      csvAmount(parseMoneyToCents(product.revenue)),
    ].join(CSV_DELIMITER),
  )
  return `${UTF8_BOM}${[header, ...rows].join('\r\n')}\r\n`
}

export function csvFileName(range: DateRange): string {
  return `statistiky-${range.from}-${range.to}.csv`
}

/**
 * The media type, with the delimiter declared.
 *
 * RFC 4180 lets a CSV name its separator, and a spreadsheet that reads it opens a semicolon-delimited
 * file without asking. Legacy sends `text/csv;charset=utf-8;` with a trailing semicolon and no
 * delimiter, which says nothing.
 */
export function productCsvMediaType(): string {
  return `text/csv;charset=utf-8;header=present;delimiter=${CSV_DELIMITER}`
}

/** How many of these names would have broken a naive comma-delimited export. */
export function namesNeedingQuotes(products: ProductStat[]): number {
  return products.filter((product) => /["\r\n;,]/.test(product.productName ?? '')).length
}

// ─── FB-106: performance ─────────────────────────────────────────────────────

export interface Performance {
  id?: number
  name?: string
  systemRole?: string
  position?: string
  ordersCount?: number
  totalRevenue?: MoneyInput
  avgOrderValue?: MoneyInput
  hoursWorked?: number
  ordersPerHour?: number
  avgDeliveryTime?: number | null
  totalKm?: number | null
}

export interface PerformanceRow extends Performance {
  /** `null` when nobody clocked them in — not nought. */
  perHour: number | null
  revenueCents: number
}

/**
 * The performance rows, with an unmeasurable rate left unmeasured.
 *
 * **FB-106.** `hoursWorked` is 0 for seven of the thirteen employees in this month, and they carried
 * 906 of the 2 259 orders between them — the busiest of them 328. `ordersPerHour` comes back as `0.0`
 * for all seven, and rendering it straight says they did nothing per hour when it means nobody clocked
 * them in. `null` here, *nemerané* on the screen, and last in the sort rather than bottom of it.
 */
export function performanceRows(rows: Performance[]): PerformanceRow[] {
  return rows.map((row) => ({
    ...row,
    perHour: (row.hoursWorked ?? 0) > 0 ? (row.ordersPerHour ?? 0) : null,
    revenueCents: parseMoneyToCents(row.totalRevenue),
  }))
}

export interface PerformanceTotals {
  staff: number
  orders: number
  revenueCents: number
  /** Rows whose rate cannot be computed, and the orders they carried anyway. */
  withoutHours: number
  ordersWithoutHours: number
}

export function performanceTotals(rows: PerformanceRow[]): PerformanceTotals {
  let orders = 0
  let revenueCents = 0
  let withoutHours = 0
  let ordersWithoutHours = 0

  for (const row of rows) {
    orders += row.ordersCount ?? 0
    revenueCents += row.revenueCents
    if (row.perHour === null && (row.ordersCount ?? 0) > 0) {
      withoutHours += 1
      ordersWithoutHours += row.ordersCount ?? 0
    }
  }

  return { staff: rows.length, orders, revenueCents, withoutHours, ordersWithoutHours }
}

export type PerformanceSort = 'orders' | 'revenue' | 'per-hour'

/** A row with no measurable rate sorts after every measured one, never among the worst. */
export function sortPerformance(rows: PerformanceRow[], key: PerformanceSort): PerformanceRow[] {
  return [...rows].sort((left, right) => {
    if (key === 'orders') return (right.ordersCount ?? 0) - (left.ordersCount ?? 0)
    if (key === 'revenue') return right.revenueCents - left.revenueCents
    if (left.perHour === null && right.perHour === null) {
      return (right.ordersCount ?? 0) - (left.ordersCount ?? 0)
    }
    if (left.perHour === null) return 1
    if (right.perHour === null) return -1
    return right.perHour - left.perHour
  })
}

// ─── the heat map ────────────────────────────────────────────────────────────

export interface Heatmap {
  heatmap?: number[][]
}

export interface HeatCell {
  weekday: number
  hour: number
  orders: number
  /** 0…1 against the busiest cell, for the shading. */
  intensity: number
}

/**
 * Said in a card whose period holds nothing — FB-200, §9.103.
 *
 * Until 2026-08-11 the three period cards were each wrapped in `rows.length > 0 &&` and disappeared
 * instead. The default period is `today`, which is empty every morning, so the owner opened the page
 * and reported the heatmap both as *not working* and as *never built* — an absent card cannot say
 * which of those it is. Legacy renders `Žiadne dáta` in the same box.
 */
export function emptyPeriodNote(): string {
  return tt('Za zvolené obdobie tu nie sú žiadne objednávky.')
}

/** One day bar's reading, as the hover panel says it. */
export interface DayBarReading {
  date: string
  label: string
  revenueCents: number
  orders: number
}

/**
 * What a bar is worth, read once for the hover panel and the `title` both.
 *
 * The bars carried only a native `title`: it waits about a second, draws by the cursor rather than
 * the bar, and takes no styling — *"not hoverable, I cant see tržba in that day and also what day it
 * was"*. The panel above the chart uses this; `title` keeps it for the keyboard path.
 */
export function dayBarReading(day: {
  date?: string
  revenue?: MoneyInput
  orders?: number
}): DayBarReading {
  const date = day.date ?? ''
  return {
    date,
    label: formatDayLabel(date),
    revenueCents: Math.round(Number(day.revenue ?? 0) * 100),
    orders: day.orders ?? 0,
  }
}

/** Today's takings against yesterday's. */
export interface DayComparison {
  todayCents: number
  yesterdayCents: number
  todayOrders: number
  yesterdayOrders: number
  differenceCents: number
  /** `null` when yesterday took nothing — a share of zero is not 0 % and not ∞. */
  changeBasisPoints: number | null
}

/**
 * `statistics.today_vs_yesterday`, which this franchise has switched on and no AGOS screen drew.
 *
 * <p><b>Whole days, and the panel says whole days.</b> Legacy's subtitle claims it compares *"do
 * rovnakej hodiny"* while both of its calls send whole-day ranges — so it puts a finished yesterday
 * beside a part-formed today and describes it as like-for-like. The numbers here are the same two
 * whole days; the copy is not carried over.
 */
export function compareDays(today: SalesStatistics, yesterday: SalesStatistics): DayComparison {
  const now = salesTotals(today)
  const before = salesTotals(yesterday)
  return {
    todayCents: now.revenueCents,
    yesterdayCents: before.revenueCents,
    todayOrders: now.orders,
    yesterdayOrders: before.orders,
    differenceCents: now.revenueCents - before.revenueCents,
    changeBasisPoints:
      before.revenueCents > 0
        ? Math.round(((now.revenueCents - before.revenueCents) / before.revenueCents) * 10_000)
        : null,
  }
}

// ─── the VAT split ───────────────────────────────────────────────────────────

/** One row of `GET /statistics/vat` — `BACKEND.md` §10A.64. */
export interface VatStatistic {
  vatRate?: MoneyInput
  vatLetter?: string | null
  categoryName?: string | null
  lines?: number
  orders?: number
  revenue?: MoneyInput
  netRevenue?: MoneyInput
  vatAmount?: MoneyInput
}

export interface VatRow {
  key: string
  label: string
  categoryName: string
  lines: number
  orders: number
  revenueCents: number
  netCents: number
  vatCents: number
}

/**
 * How a rate is named on the card — §9.105.
 *
 * <p>The rate is what groups these rows, not the letter: a rate that changed mid-period produces two
 * rows under one letter, and that is the report being right rather than a display bug. A line that
 * never recorded a snapshot arrives as `unknown` and is named as such — the server does not attribute
 * it to a rate, and neither does this.
 */
export function vatRowLabel(row: VatStatistic): string {
  const letter = (row.vatLetter ?? '').trim()
  if (row.vatRate === undefined || row.vatRate === null) {
    return letter === '' || letter === 'unknown' ? tt('Bez sadzby') : letter
  }
  const rate = `${Number(row.vatRate)} %`
  return letter === '' || letter === 'unknown' ? rate : `${letter} - ${rate}`
}

/** The rows as the card draws them, biggest takings first. */
export function vatRows(data: unknown): VatRow[] {
  const rows = Array.isArray(data) ? (data as VatStatistic[]) : []
  return rows
    .map((row, index) => ({
      // The rate is the identity; the index only breaks a tie the server should never send.
      key: `${row.vatLetter ?? 'unknown'}-${row.vatRate ?? 'none'}-${index}`,
      label: vatRowLabel(row),
      categoryName: (row.categoryName ?? '').trim(),
      lines: row.lines ?? 0,
      orders: row.orders ?? 0,
      revenueCents: parseMoneyToCents(row.revenue),
      netCents: parseMoneyToCents(row.netRevenue),
      vatCents: parseMoneyToCents(row.vatAmount),
    }))
    .sort((left, right) => right.revenueCents - left.revenueCents)
}

/** What the card totals, so a disagreement with the products card is visible on screen. */
export function vatTotals(rows: readonly VatRow[]): {
  revenueCents: number
  netCents: number
  vatCents: number
} {
  return rows.reduce(
    (total, row) => ({
      revenueCents: total.revenueCents + row.revenueCents,
      netCents: total.netCents + row.netCents,
      vatCents: total.vatCents + row.vatCents,
    }),
    { revenueCents: 0, netCents: 0, vatCents: 0 },
  )
}

/** `[weekday][hour]` as cells, with the busiest hour of the week found once. */
export function heatCells(data: Heatmap): { cells: HeatCell[]; busiest: HeatCell | null } {
  const grid = data.heatmap ?? []
  let peak = 0
  for (const row of grid) {
    for (const value of row) peak = Math.max(peak, value)
  }

  const cells: HeatCell[] = []
  let busiest: HeatCell | null = null
  for (let weekday = 0; weekday < grid.length; weekday += 1) {
    const row = grid[weekday] ?? []
    for (let hour = 0; hour < row.length; hour += 1) {
      const orders = row[hour] ?? 0
      const cell = { weekday, hour, orders, intensity: peak > 0 ? orders / peak : 0 }
      cells.push(cell)
      if (busiest === null || orders > busiest.orders) busiest = cell
    }
  }
  return { cells, busiest }
}

/** The grid arrives Sunday-first; Slovak weeks start on Monday. */
export const WEEKDAY_LABELS = [tt('Po'), tt('Ut'), tt('St'), tt('Št'), tt('Pi'), tt('So'), tt('Ne')]

export function weekdayLabel(weekday: number): string {
  // 0 is Sunday in the payload, which is index 6 in a Monday-first week.
  return WEEKDAY_LABELS[(weekday + 6) % 7] ?? ''
}

// ─── payment methods ─────────────────────────────────────────────────────────

const PAYMENT_LABELS: Record<string, string> = {
  cash: tt('Hotovosť'),
  card_terminal: tt('Karta - terminál'),
  card_courier: tt('Karta u kuriéra'),
  card_online: tt('CardPay online'),
  card: tt('Karta'),
  other: tt('Iné'),
}

export function paymentLabel(method: string | undefined): string {
  return PAYMENT_LABELS[method ?? ''] ?? (method ?? '')
}

/**
 * A colour per payment method — §9.195, legacy's `PAYMENT_META` to the hex.
 *
 * **Copied rather than re-chosen.** An operator who has read this screen for a year knows green is
 * cash; picking new colours for the same data would be a change with no upside.
 *
 * An unrecognised method is grey rather than absent: a payment the report cannot name still took
 * money, and dropping it from the chart would make the shares add up to less than the total.
 */
const PAYMENT_COLOURS: Record<string, string> = {
  cash: '#10b981',
  card_terminal: '#3b82f6',
  card_courier: '#f97316',
  card_online: '#8b5cf6',
  card: '#60a5fa',
  other: '#6b7280',
}

export const UNKNOWN_PAYMENT_COLOUR = '#6b7280'

export function paymentColour(method: string | undefined): string {
  return PAYMENT_COLOURS[method ?? ''] ?? UNKNOWN_PAYMENT_COLOUR
}

export interface PaymentSlice {
  method: string
  label: string
  amountCents: number
  shareBasisPoints: number
}

export function paymentSlices(sales: SalesStatistics): PaymentSlice[] {
  const total = parseMoneyToCents(sales.totalRevenue)
  return (sales.revenueByPaymentMethod ?? [])
    .map((method) => {
      const amountCents = parseMoneyToCents(method.amount)
      return {
        method: method.method ?? '',
        label: paymentLabel(method.method),
        amountCents,
        shareBasisPoints: total > 0 ? Math.round((amountCents / total) * 10_000) : 0,
      }
    })
    .sort((left, right) => right.amountCents - left.amountCents)
}

// ─── comparison ──────────────────────────────────────────────────────────────

export interface Comparison {
  currentCents: number
  previousCents: number
  differenceCents: number
  /** `null` when there is nothing to compare against. */
  changeBasisPoints: number | null
}

/**
 * This period against the one before it, the same length.
 *
 * Legacy compares today against yesterday clipped to the same time of day, computed in a `useMemo`
 * with an empty dependency list — so the two ISO instants are fixed at mount and never move. On a
 * screen left open through a shift the comparison is against whenever the page was loaded, and
 * because the instants are also the query keys nothing refetches. Here the comparison follows the
 * period being looked at, which needs no clock at all.
 */
export function compare(current: SalesStatistics, previous: SalesStatistics): Comparison {
  const currentCents = parseMoneyToCents(current.totalRevenue)
  const previousCents = parseMoneyToCents(previous.totalRevenue)
  return {
    currentCents,
    previousCents,
    differenceCents: currentCents - previousCents,
    changeBasisPoints:
      previousCents > 0
        ? Math.round(((currentCents - previousCents) / previousCents) * 10_000)
        : null,
  }
}

/** The period immediately before this one, the same number of days. */
export function previousRange(range: DateRange): DateRange {
  const from = new Date(`${range.from}T12:00:00`)
  const to = new Date(`${range.to}T12:00:00`)
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1
  const previousTo = new Date(from)
  previousTo.setDate(previousTo.getDate() - 1)
  const previousFrom = new Date(previousTo)
  previousFrom.setDate(previousFrom.getDate() - (days - 1))
  return { from: localDate(previousFrom), to: localDate(previousTo) }
}

export function formatShare(basisPoints: number): string {
  return `${String(Math.round(basisPoints) / 100).replace('.', ',')} %`
}

export function formatChange(basisPoints: number | null): string {
  if (basisPoints === null) return '-'
  const value = Math.round(basisPoints) / 100
  const sign = value > 0 ? '+' : ''
  return `${sign}${String(value).replace('.', ',')} %`
}

/** `2,48` — a rate, not money. */
export function formatRate(value: number | null): string {
  if (value === null) return tt('nemerané')
  return String(Math.round(value * 100) / 100).replace('.', ',')
}

export function formatDayLabel(date: string | undefined): string {
  const text = (date ?? '').trim()
  if (text === '') return ''
  const parts = text.split('-')
  if (parts.length !== 3) return text
  return `${Number(parts[2])}.${Number(parts[1])}.`
}

export { formatCents, formatCount }

/**
 * The seven shades a heatmap cell can take — §9.194, ported from legacy's `heatmapColor`.
 *
 * **Seven steps and not a continuous ramp, deliberately.** A linear opacity makes neighbouring hours
 * indistinguishable at a glance, and the legend printed under the grid has to correspond to
 * something a cell can actually be. Legacy's thresholds exactly: 0.15 / 0.3 / 0.5 / 0.7 / 0.85.
 *
 * Nought is not the lightest *shade of something* — it is its own step, so an hour with no orders
 * reads as empty rather than as very quiet.
 */
export const HEAT_STEPS = [0.06, 0.12, 0.25, 0.4, 0.6, 0.78, 0.95] as const

export function heatOpacity(orders: number, peak: number): number {
  if (peak === 0 || orders === 0) return HEAT_STEPS[0]
  const intensity = orders / peak
  if (intensity < 0.15) return HEAT_STEPS[1]
  if (intensity < 0.3) return HEAT_STEPS[2]
  if (intensity < 0.5) return HEAT_STEPS[3]
  if (intensity < 0.7) return HEAT_STEPS[4]
  if (intensity < 0.85) return HEAT_STEPS[5]
  return HEAT_STEPS[6]
}

/**
 * Whether a cell's number needs light text — §9.194.
 *
 * Legacy's rule, and it is the detail that makes printing the count worth doing: past halfway the
 * fill is dark enough that dark text disappears into it.
 */
export function heatTextIsLight(orders: number, peak: number): boolean {
  return orders > 0 && peak > 0 && orders / peak > 0.5
}

/**
 * Legacy's twelve-colour cycle for category groups — §9.196.
 *
 * By index rather than by name: the groups are ordered by revenue, so the biggest category is always
 * amber and the eye learns the ordering. Keyed by name instead, a quiet week would repaint the whole
 * panel.
 */
export const CATEGORY_COLOURS = [
  '#f59e0b',
  '#3b82f6',
  '#10b981',
  '#8b5cf6',
  '#ef4444',
  '#ec4899',
  '#14b8a6',
  '#f97316',
  '#84cc16',
  '#06b6d4',
  '#a855f7',
  '#6366f1',
] as const

export function categoryColour(index: number): string {
  return CATEGORY_COLOURS[index % CATEGORY_COLOURS.length] ?? CATEGORY_COLOURS[0]
}

/**
 * What share of the period a cents figure is, in basis points — §9.196.
 *
 * **Not `shareBasisPoints`, which already exists and takes a *product*.** This one takes a total, for
 * the category headers: a group's revenue is a sum, not a row. Two functions rather than one
 * overload because the existing one parses `product.revenue` and this one is handed cents.
 *
 * Nought when the total is nought — a period with no revenue has no shares, and dividing by it would
 * print `NaN %`.
 */
export function centsShareBasisPoints(partCents: number, totalCents: number): number {
  if (totalCents <= 0) return 0
  return Math.round((partCents / totalCents) * 10_000)
}

/**
 * The gridlines a day chart is read against — §9.197.
 *
 * **The bars had no scale at all.** Four lines and a baseline, at even fractions of the peak, so a
 * bar can be read as a figure rather than only compared with its neighbours.
 *
 * Descending, because that is the order they are drawn in from the top of the chart.
 */
export function chartGridlines(peakCents: number, lines = 4): number[] {
  if (peakCents <= 0) return []
  return Array.from({ length: lines }, (_, index) => Math.round((peakCents * (lines - index)) / lines))
}

/**
 * How often a day label is printed — §9.197.
 *
 * The chart printed labels only for a window of **fourteen days or fewer**, so the twenty-eight-day
 * view — the one anybody opens — had none at all, and nothing on screen said a threshold had been
 * crossed. Thinning keeps a label whatever the window is.
 */
export function labelEvery(days: number): number {
  if (days <= 14) return 1
  if (days <= 31) return 2
  if (days <= 62) return 4
  return Math.ceil(days / 16)
}

/**
 * Orders per hour across the staff whose hours are known — §9.197.
 *
 * **Not an average over everybody.** FB-106 found seven of thirteen with no hours recorded against
 * 906 orders; dividing the whole by the whole would report a rate diluted by exactly the people that
 * panel's warning exists to name. Null when nobody has measurable hours, because there is then no
 * figure — not a nought.
 */
export function averagePerHour(rows: PerformanceRow[]): number | null {
  const measured = rows.filter((row) => row.perHour !== null)
  if (measured.length === 0) return null
  const total = measured.reduce((sum, row) => sum + (row.perHour ?? 0), 0)
  return total / measured.length
}
